// Analytics tracking utility
import { createTelemetryService } from '$lib/telemetry';

// Create telemetry service instance
const telemetry = typeof window !== 'undefined' ? createTelemetryService() : null;

/**
 * The payload each tracked event carries.
 *
 * Loose on purpose: these are analytics dimensions, assembled at the call site
 * and flattened into the event. Naming them keeps the fields honest without
 * pretending the set is closed.
 */
interface TrackingData {
  [key: string]: unknown;
}

/**
 * Track events via telemetry service
 *
 * Each function used to assemble a second, richer payload — with a timestamp
 * and a session id — for a `logToBackend(event)` call that is commented out in
 * all seven and whose function exists nowhere in the codebase. Those objects
 * were built and discarded on every event, so they are gone; what reaches the
 * telemetry service is unchanged.
 */
export const track = {
  /**
   * Search performed event
   */
  async searchPerformed(data: TrackingData) {
    // Track via telemetry service
    if (telemetry) {
      await telemetry.event('search_performed', {
        search_mode: data.mode,
        result_count: data.resultCount,
        company_count: data.companyCount,
        contact_count: data.contactCount
      });
    }
  },

  /**
   * Filter applied event
   */
  async filterApplied(data: TrackingData) {
    if (telemetry) {
      await telemetry.event('filter_applied', {
        filter_type: data.filterType,
        search_mode: data.mode
      });
    }
  },

  /**
   * AI refinement used
   */
  async aiRefinementUsed(data: TrackingData) {
    if (telemetry) {
      await telemetry.event('ai_refinement_used', {
        search_mode: data.mode,
        improvement:
          Number(data.newResultCount) > Number(data.previousResultCount) ? 'increased' : 'decreased'
      });
    }
  },

  /**
   * Order started (checkout initiated)
   */
  async orderStarted(data: TrackingData) {
    if (telemetry) {
      await telemetry.event('begin_checkout', {
        currency: 'CHF',
        value: data.estimatedValue,
        company_count: data.companyCount,
        contact_count: data.contactCount
      });
    }
  },

  /**
   * Order completed (purchase)
   */
  async orderCompleted(data: TrackingData) {
    if (telemetry) {
      await telemetry.event('purchase', {
        transaction_id: data.orderId,
        currency: 'CHF',
        value: data.totalValue,
        company_count: data.companyCount,
        contact_count: data.contactCount
      });
    }
  },

  /**
   * Mode switched
   */
  async modeSwitched(data: TrackingData) {
    if (telemetry) {
      await telemetry.event('mode_switched', {
        from_mode: data.fromMode,
        to_mode: data.toMode
      });
    }
  },

  /**
   * Neighbor samples added
   */
  async neighborSampleAdded(data: TrackingData) {
    if (telemetry) {
      await telemetry.event('neighbor_sample_added', {
        sample_count: data.totalSamples
      });
    }
  }
};

/**
 * Send event to backend for logging
 * Currently disabled
 */
// async function logToBackend(event) {
//   try {
//     await fetch('/api/analytics/track', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json'
//       },
//       body: JSON.stringify(event)
//     });
//   } catch (error) {
//     console.error('Failed to log event to backend:', error);
//   }
// }

/**
 * Get user ID (persistent across sessions)
 */
